/**
 * ChoiceScreen — mirrors FOM mobile ChoiceScreen
 *
 * Landing/menu page shown after login on mobile. User picks one of:
 *   - Daily Posting  → /posting
 *   - Summary        → MobileCommandView (command center)
 *   - Staff          → OfficerTaskView (individual shift view)
 *
 * Bottom: Clear Shift Data (exec only) + Sign Out
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, isExecRole } from '../../stores/auth'
import { workerPostV1 } from '../../api/worker'

interface Props {
  onChoice: (view: 'command' | 'officer') => void
}

function firstName(full: string): string {
  if (!full) return ''
  const s = full.trim()
  if (s.includes(',')) return s.split(',')[0].trim()
  const parts = s.split(/\s+/)
  return parts[parts.length - 1]
}

export default function ChoiceScreen({ onChoice }: Props) {
  const session = useAuthStore(s => s.session)
  const signOut = useAuthStore(s => s.signOut)
  const navigate = useNavigate()
  const userName = firstName(session?.name ?? session?.callSign ?? '')
  const isExec = isExecRole(session?.rank ?? '')

  // Officers skip the choice screen — go straight to their own shift view
  useEffect(() => {
    if (!isExec) onChoice('officer')
  }, [isExec])

  if (!isExec) return null

  async function clearShiftData() {
    if (!confirm('Clear all shift data from the cloud? This cannot be undone.')) return
    if (!session?.token) { alert('Not logged in'); return }
    try {
      const res = await workerPostV1<{ ok?: boolean; error?: string; clearSignal?: string }>('/api/v1/shift/clear')
      if (res.ok) {
        try { localStorage.removeItem('fom_mobile_cache') } catch { /* silent */ }
        alert('Shift data cleared. Roster preserved.')
        location.reload()
      } else {
        alert(res.error ?? 'Failed')
      }
    } catch (err) {
      alert('Network error: ' + (err instanceof Error ? err.message : String(err)))
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      paddingTop: 'calc(24px + env(safe-area-inset-top))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
      background: 'linear-gradient(160deg,#0a1628,#162040,#0a1628)',
      color: 'var(--text-primary)',
      overflowY: 'auto',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>

        {/* Castle badge */}
        <img
          src="./castle-logo.png"
          alt="Castellan"
          style={{
            width: 96, height: 96, margin: '0 auto 14px',
            display: 'block', borderRadius: 16, objectFit: 'contain',
            boxShadow: '0 4px 16px rgba(201,168,76,0.3)',
          }}
        />

        {/* Welcome heading */}
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 4, fontStyle: 'italic' }}>
          Welcome, {userName}
        </h1>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 4 }}>{session?.rank}</div>
        <div style={{ fontSize: 12, color: 'var(--text-hint)', marginBottom: 6 }}>Fair Oaks Mall Security</div>
        <a
          href="#"
          onClick={(e) => { e.preventDefault() }}
          style={{
            color: '#60a5fa', fontSize: 13, fontWeight: 600, textDecoration: 'underline',
            display: 'inline-block', marginBottom: 28,
          }}
        >Edit Profile</a>

        {/* Choice cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
          {/* Daily Posting */}
          <button
            onClick={() => navigate('/posting')}
            style={{
              display: 'flex', alignItems: 'center', gap: 16, width: '100%',
              padding: '18px 20px',
              background: 'linear-gradient(145deg,#162040,#1e3050)',
              border: '2px solid #c9a84c', borderRadius: 16,
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 10, background: 'var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, flexShrink: 0,
            }}>📋</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Daily Posting</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Shift assignments</div>
            </div>
          </button>

          {/* Summary (Command) */}
          <button
            onClick={() => onChoice('command')}
            style={{
              display: 'flex', alignItems: 'center', gap: 16, width: '100%',
              padding: '18px 20px',
              background: 'linear-gradient(145deg,#162040,#1e3050)',
              border: '2px solid #059669', borderRadius: 16,
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 10, background: 'var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, flexShrink: 0,
            }}>🛡️</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Summary</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Operations overview</div>
            </div>
          </button>

          {/* Staff (Officer) */}
          <button
            onClick={() => onChoice('officer')}
            style={{
              display: 'flex', alignItems: 'center', gap: 16, width: '100%',
              padding: '18px 20px',
              background: 'linear-gradient(145deg,#162040,#1e3050)',
              border: '2px solid #2563eb', borderRadius: 16,
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 10, background: 'var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, flexShrink: 0,
            }}>👮</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>Staff</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Individual shift view</div>
            </div>
          </button>
        </div>

        {/* Bottom buttons */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'stretch',
          gap: 10, marginTop: 24, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto',
        }}>
          {isExec && (
            <button
              onClick={clearShiftData}
              style={{
                background: 'none', border: '1px solid #ef444440', borderRadius: 10,
                padding: '10px 24px', color: '#ef4444', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', width: '100%', display: 'block', textAlign: 'center',
              }}
            >🗑️ Clear Shift Data</button>
          )}
          <button
            onClick={signOut}
            style={{
              background: 'none', border: '1px solid #334155', borderRadius: 10,
              padding: '10px 24px', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', width: '100%', display: 'block', textAlign: 'center',
            }}
          >Sign Out</button>
        </div>
      </div>
    </div>
  )
}
